import { useMemo } from 'react';
import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import { Progress as FlowBiteProgressBar } from 'flowbite-react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'next-i18next';

import { useAuth } from '@restworld/feature-authentication';
import { cn, useResponsiveBreakPoints, getAssetSrc } from '@restworld/utils-common';
import { useWizard } from '@restworld/utils-common';
import { Card } from '@restworld/ui-components';
import { Heading, Logo } from '@restworld/ui-ds-base';

import { PreviousButton } from './form-inputs.components';

export const Header = ({
  mainTitle,
  completedPercentage,
  info,
  entryTime,
}: {
  mainTitle?: string;
  completedPercentage: number;
  info: string;
  entryTime?: number;
}) => {
  const { activeStep } = useWizard();
  const { getValues } = useFormContext();
  const { user } = useAuth();
  const { isDesktop } = useResponsiveBreakPoints();
  const workerName = useMemo(() => user?.name || getValues('firstName') || '', [getValues, user?.name]);
  const { t } = useTranslation();
  return (
    <div className="px-6 flex flex-col gap-6 md:w-[50%] md:bg-primary-800 md:h-full">
      <div className="flex py-6 items-center justify-center relative md:justify-start">
        <div className="absolute left-0 md:hidden">
          <PreviousButton isMobile entryTime={entryTime} />
        </div>
        <Logo variant="horizontal" title="restworld" className="h-9 w-auto text-white" />
      </div>
      <div className="flex flex-col gap-6 md:h-full md:justify-center md:px-[3rem]">
        {/* Match master's old-ds Title: superSuperSuperLarge = text-5xl (48px) desktop / superLarge =
            text-2xl (24px) mobile, weight normal. ui-ds-base Heading size 1 is only text-4xl (36px)
            and defaults to semibold, so bump the desktop size and force normal weight. */}
        <Heading size={isDesktop ? 1 : 3} color="white" weight="normal" className="lg:text-5xl lg:leading-none">
          <span className="flex flex-col">
            {activeStep <= 3 && <span>{`${t('common:lets_begin')}${workerName ? ' ' : ''}${workerName}`}!</span>}
            <span>{mainTitle}</span>
          </span>
        </Heading>
        <div className="flex flex-col gap-2">
          <ProgressStyled progress={completedPercentage} />
          <Heading size={6} color="white">
            <span role="img" aria-labelledby="percentage-complete">
              🎉{completedPercentage}% {t('common:completed')}
            </span>
          </Heading>
        </div>
        <CardWrapper>
          <Card variant="primary100" className={'min-h-fit'}>
            <div className="flex gap-2 flex-col">
              <div className="flex gap-1">
                <QuestionMarkCircleIcon className="h-6 w-6 text-primary-800" />
                <Heading>{t('common:why_this_information')}</Heading>
              </div>
              <Heading size={5} color="muted" weight="normal">
                {info}
              </Heading>
            </div>
          </Card>
        </CardWrapper>
      </div>
    </div>
  );
};
const CardWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('w-full px-0', className)} {...props} />
);
const ProgressStyled = ({ className, ...props }: React.ComponentProps<typeof FlowBiteProgressBar>) => (
  <FlowBiteProgressBar
    // flowbite applies theme.size[size] (md = h-2.5) to BOTH the track and the fill, and it beats
    // a plain `h-1` className -> a thick track at 0%. Override the size so both are h-1 (master's
    // thin bar).
    theme={{ size: { md: 'h-1' } }}
    className={cn('[&>div:first-child]:bg-secondary-800 [&>div:first-child]:shadow', className)}
    {...props}
  />
);
